import {
  type TableData,
  extractChartSpec,
  extractTableData,
  formatCellValue,
  injectRowsIntoSpec,
} from '@spotify/confidence-conversation-react';
import {
  isChartBlockTool,
  isNumberBlockTool,
  isTableBlockTool,
} from '@spotify/confidence-core-react';
import {
  type ChatEventFragment,
  PulseV1PulseBlockType,
} from '@spotify/confidence-graphql';

export type InlineTableBlock = {
  type: PulseV1PulseBlockType.PulseBlockTypeTable;
  table: TableData;
  eventName: string;
};

export type InlineChartBlock = {
  type: PulseV1PulseBlockType.PulseBlockTypeChart;
  spec: string;
  name: string;
  eventName: string;
};

export type InlineNumberBlock = {
  type: PulseV1PulseBlockType.PulseBlockTypeBigNumber;
  value: string;
  name: string;
  eventName: string;
};

export type InlineBlockData =
  | InlineTableBlock
  | InlineChartBlock
  | InlineNumberBlock;

type InlineBlockEntry = {
  eventIndex: number;
  block: InlineBlockData;
};

export type InlineBlocksIndex = {
  blocksByName: ReadonlyMap<string, readonly InlineBlockEntry[]>;
  eventIndexByName: ReadonlyMap<string, number>;
};

export const EMPTY_INLINE_BLOCKS_INDEX: InlineBlocksIndex = {
  blocksByName: new Map(),
  eventIndexByName: new Map(),
};

const injectDataIntoSpec = (
  spec: string,
  input: Record<string, unknown>,
): string => {
  const tableData = extractTableData({
    content: '',
    metadata: { toolInput: input },
  });
  return injectRowsIntoSpec(spec, tableData?.rows);
};

const extractBlock = (event: ChatEventFragment): InlineBlockData | null => {
  const toolCall = event.toolCall;
  if (!toolCall) return null;

  const input = toolCall.input as Record<string, unknown> | undefined;
  if (!input) return null;

  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (!name) return null;

  if (isTableBlockTool(toolCall.tool)) {
    const tableData = extractTableData({
      content: '',
      metadata: { toolInput: input },
    });
    if (!tableData) return null;
    return {
      type: PulseV1PulseBlockType.PulseBlockTypeTable,
      table: tableData,
      eventName: event.name,
    };
  }

  if (isChartBlockTool(toolCall.tool)) {
    const rawSpec = extractChartSpec({
      content: '',
      metadata: { toolInput: input },
    });
    if (!rawSpec) return null;
    return {
      type: PulseV1PulseBlockType.PulseBlockTypeChart,
      spec: injectDataIntoSpec(rawSpec, input),
      name,
      eventName: event.name,
    };
  }

  if (isNumberBlockTool(toolCall.tool)) {
    const value =
      typeof input.value === 'string' || typeof input.value === 'number'
        ? String(input.value).trim()
        : '';
    if (!value) return null;
    return {
      type: PulseV1PulseBlockType.PulseBlockTypeBigNumber,
      value,
      name,
      eventName: event.name,
    };
  }

  return null;
};

const blockName = (block: InlineBlockData): string =>
  block.type === PulseV1PulseBlockType.PulseBlockTypeTable
    ? block.table.name
    : block.name;

export const buildInlineBlocksIndex = (
  events: readonly ChatEventFragment[],
): InlineBlocksIndex => {
  const blocksByName = new Map<string, InlineBlockEntry[]>();
  const eventIndexByName = new Map<string, number>();

  events.forEach((event, eventIndex) => {
    eventIndexByName.set(event.name, eventIndex);

    const block = extractBlock(event);
    if (!block) return;

    const name = blockName(block).trim();
    const entries = blocksByName.get(name);
    if (entries) {
      entries.push({ eventIndex, block });
    } else {
      blocksByName.set(name, [{ eventIndex, block }]);
    }
  });

  return { blocksByName, eventIndexByName };
};

const resolveInlineBlockOfKind = <T extends InlineBlockData>(
  index: InlineBlocksIndex,
  name: string,
  messageEventName: string | undefined,
  matches: (block: InlineBlockData) => block is T,
): T | undefined => {
  const entries = index.blocksByName
    .get(name)
    ?.filter((entry): entry is InlineBlockEntry & { block: T } =>
      matches(entry.block),
    );
  if (!entries || entries.length === 0) return undefined;

  const messageIndex = messageEventName
    ? index.eventIndexByName.get(messageEventName)
    : undefined;
  if (messageIndex === undefined) return entries[entries.length - 1].block;

  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (entries[i].eventIndex <= messageIndex) return entries[i].block;
  }
  return entries[0].block;
};

const isNumberBlock = (block: InlineBlockData): block is InlineNumberBlock =>
  block.type === PulseV1PulseBlockType.PulseBlockTypeBigNumber;

const isMarkerBlock = (
  block: InlineBlockData,
): block is InlineTableBlock | InlineChartBlock => !isNumberBlock(block);

export const resolveInlineBlock = (
  index: InlineBlocksIndex,
  name: string,
  messageEventName?: string,
): InlineTableBlock | InlineChartBlock | undefined =>
  resolveInlineBlockOfKind(index, name, messageEventName, isMarkerBlock);

export const resolveInlineNumberBlock = (
  index: InlineBlocksIndex,
  name: string,
  messageEventName?: string,
): InlineNumberBlock | undefined =>
  resolveInlineBlockOfKind(index, name, messageEventName, isNumberBlock);

export const blockToMarkdownTable = (block: InlineBlockData): string => {
  if (block.type !== PulseV1PulseBlockType.PulseBlockTypeTable) return '';
  const { columns, rows } = block.table;
  const header = `| ${columns.join(' | ')} |`;
  const separator = `| ${columns.map(() => '---').join(' | ')} |`;
  const body = rows
    .map(
      row => `| ${columns.map(col => formatCellValue(row[col])).join(' | ')} |`,
    )
    .join('\n');
  return `${header}\n${separator}\n${body}`;
};
